import type { SupabaseClient } from '@supabase/supabase-js';

export type ReviewWithAuthor = {
    id: string;
    rating: number;
    comment: string | null;
    createdAt: string;
    author: { id: string; name: string; avatar: string | null } | null;
};

export type ReviewViewer = {
    id: string;
    name: string;
    avatar?: string | null;
};

function coerceAuthor(raw: unknown): ReviewWithAuthor['author'] {
    if (raw == null) return null;
    const o = Array.isArray(raw) ? raw[0] : raw;
    if (!o || typeof o !== 'object') return null;
    const r = o as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id : '';
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    const avatar = r.avatar != null && typeof r.avatar === 'string' ? r.avatar : null;
    if (!id && !name) return null;
    return { id, name, avatar };
}

function authorFromViewer(viewer: ReviewViewer): NonNullable<ReviewWithAuthor['author']> {
    return {
        id: viewer.id,
        name: viewer.name.trim(),
        avatar: viewer.avatar ?? null,
    };
}

function needsViewerNameFallback(
    author: ReviewWithAuthor['author'],
    userId: string,
    viewer: ReviewViewer,
): boolean {
    if (!userId || userId !== viewer.id) return false;
    if (!author) return true;
    if (!author.name.trim()) return true;
    return false;
}

function normalizeReviewRow(row: Record<string, unknown>, viewer?: ReviewViewer | null): ReviewWithAuthor {
    const userId = typeof row.userId === 'string' ? row.userId : '';
    let author = coerceAuthor(row.author ?? row.user ?? row.User);
    if (viewer && needsViewerNameFallback(author, userId, viewer)) {
        author = authorFromViewer(viewer);
    }
    return {
        id: String(row.id),
        rating: Number(row.rating),
        comment: row.comment != null ? String(row.comment) : null,
        createdAt: String(row.createdAt),
        author,
    };
}

export function reviewAuthorDisplayName(review: ReviewWithAuthor): string {
    const n = review.author?.name?.trim();
    return n || 'Usuário';
}

export type FetchEstablishmentReviewsOpts = {
    limit?: number;
    /** Quem está logado: usado para exibir o nome quando o join com User falha (RLS) ou não existe RPC. */
    viewer?: ReviewViewer | null;
    /** Força tentativa da RPC `get_establishment_reviews` (requer migration no Supabase). */
    tryRpc?: boolean;
};

function shouldTryReviewsRpc(explicit?: boolean): boolean {
    if (explicit !== undefined) return explicit;
    return import.meta.env.VITE_USE_REVIEWS_RPC === 'true';
}

/** Lista avaliações com nome do autor. RPC opcional (sem 404 no console se não existir). */
export async function fetchEstablishmentReviews(
    supabase: SupabaseClient,
    establishmentId: string,
    opts?: FetchEstablishmentReviewsOpts,
): Promise<ReviewWithAuthor[]> {
    const limit = opts?.limit ?? 50;
    const viewer = opts?.viewer ?? null;

    if (shouldTryReviewsRpc(opts?.tryRpc)) {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('get_establishment_reviews', {
            p_establishment_id: establishmentId,
            p_limit: limit,
        });

        if (!rpcErr && Array.isArray(rpcData)) {
            return rpcData.map((r) => normalizeReviewRow(r as Record<string, unknown>, viewer));
        }
    }

    const { data, error } = await supabase
        .from('Review')
        .select('id, userId, rating, comment, createdAt, author:User(id, name, avatar)')
        .eq('establishmentId', establishmentId)
        .order('createdAt', { ascending: false })
        .limit(limit);

    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((row) => normalizeReviewRow(row, viewer));
}
